import React from 'react';
import { Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { AssistantDefinition } from '../../client/SmartAssistantBackendClient';
import { useErrorContext } from '../../contexts/ErrorContext';
import { useLoadingContext } from '../../contexts/LoadingContext';
import { PromptConfigOption } from '../../types/PromptConfigOption';

type AssistantSelectorsProps = {
  assistants: AssistantDefinition[];
  selectedAssistantInternalName: string;
  onAssistantChange: (next: string) => void;
  documentAnswerPromptConfigs: PromptConfigOption[];
  selectedDocumentAnswerPromptConfigKey: string;
  onPromptConfigChange: (next: string) => void;
  assistantPromptsError: string | null;
  includeProfile: boolean;
  onIncludeProfileChange: (next: boolean) => void;
  effectiveAwaitingResponse: boolean;
  isAssistantSelected: boolean;
};

export function AssistantSelectors({
  assistants,
  selectedAssistantInternalName,
  onAssistantChange,
  documentAnswerPromptConfigs,
  selectedDocumentAnswerPromptConfigKey,
  onPromptConfigChange,
  assistantPromptsError,
  includeProfile,
  onIncludeProfileChange,
  effectiveAwaitingResponse,
  isAssistantSelected,
}: AssistantSelectorsProps) {
  const { errors } = useErrorContext();
  const { loading, loadingText } = useLoadingContext();
  const assistantError = errors.assistants ?? null;
  return (
    <>
      <TextField
        select
        fullWidth
        label="Assistent"
        value={selectedAssistantInternalName}
        disabled={loading || assistants.length === 0}
        helperText={
          loading
            ? (loadingText || 'Lade Daten …')
            : assistantError
              ? `Assistenten konnten nicht geladen werden${assistantError ? `: ${assistantError}` : '.'}`
              : !selectedAssistantInternalName
                ? 'Bitte einen Assistenten wählen.'
                : undefined
        }
        onChange={(ev) => onAssistantChange(String(ev.target.value))}
      >
        {assistants.length === 0 ? (
          <MenuItem value="" disabled>
            Keine Assistenten verfügbar
          </MenuItem>
        ) : (
          assistants.map((assistant, idx) => (
            <MenuItem
              key={assistant.internalName ?? assistant.name ?? `assistant-${idx}`}
              value={assistant.internalName ?? ''}
            >
              {assistant.name ?? assistant.internalName ?? `Assistent ${idx + 1}`}
            </MenuItem>
          ))
        )}
      </TextField>
      {loading && (
        <Typography variant="caption" color="text.secondary">
          {loadingText || 'Lade Assistenten.'}
        </Typography>
      )}
      {documentAnswerPromptConfigs.length > 0 && (
        <TextField
          select
          fullWidth
          label="Prompt-Konfiguration"
          value={selectedDocumentAnswerPromptConfigKey}
          disabled={effectiveAwaitingResponse || !isAssistantSelected || loading}
          helperText={
            !isAssistantSelected
              ? 'Bitte zuerst einen Assistenten auswählen.'
              : loading
                ? (loadingText || 'Prompts werden geladen …')
                : assistantPromptsError
                  ? `Prompts konnten nicht geladen werden${assistantPromptsError ? `: ${assistantPromptsError}` : '.'}`
                  : undefined
          }
          onChange={(ev) => onPromptConfigChange(String(ev.target.value))}
        >
          {documentAnswerPromptConfigs.map((cfg, idx) => (
            <MenuItem key={cfg.key ?? cfg.displayName ?? `cfg-${idx}`} value={cfg.key ?? ''}>
              {cfg.displayName ?? cfg.key}
            </MenuItem>
          ))}
        </TextField>
      )}

      <FormControlLabel
        control={(
          <Checkbox
            checked={includeProfile}
            onChange={(ev) => onIncludeProfileChange(ev.target.checked)}
            disabled={!isAssistantSelected}
            color="primary"
          />
        )}
        label="Unternehmensprofil berücksichtigen"
      />
    </>
  );
}
